/**
 * Single source of truth for currency metadata across the app.
 * Used by CurrencyPicker, formatMoney, payroll dialogs, and the
 * onboarding flow.
 *
 * Order: Lyrari's v1 priority markets (JPY/USD/EUR/GBP) at the top,
 * then a regionally-balanced list of ISO 4217 active currencies the
 * payroll engine can pay people in. Codes that are no longer issued
 * (e.g. DEM, FIM) are omitted on purpose.
 */
export interface CurrencyMeta {
  code: string;       // ISO 4217
  symbol: string;     // shortest customary symbol
  name: string;       // English display name
  flag: string;       // country emoji of the largest jurisdiction
  locale: string;     // BCP 47 — used as default for Intl.NumberFormat
}

export const CURRENCIES: CurrencyMeta[] = [
  // --- pinned (Lyrari priority markets) ---
  { code: 'JPY', symbol: '¥',   name: 'Japanese Yen',           flag: '🇯🇵', locale: 'ja-JP' },
  { code: 'USD', symbol: '$',   name: 'US Dollar',              flag: '🇺🇸', locale: 'en-US' },
  { code: 'EUR', symbol: '€',   name: 'Euro',                   flag: '🇪🇺', locale: 'en-IE' },
  { code: 'GBP', symbol: '£',   name: 'British Pound',          flag: '🇬🇧', locale: 'en-GB' },

  // --- Americas ---
  { code: 'CAD', symbol: 'C$',  name: 'Canadian Dollar',        flag: '🇨🇦', locale: 'en-CA' },
  { code: 'MXN', symbol: 'Mex$',name: 'Mexican Peso',           flag: '🇲🇽', locale: 'es-MX' },
  { code: 'BRL', symbol: 'R$',  name: 'Brazilian Real',         flag: '🇧🇷', locale: 'pt-BR' },
  { code: 'ARS', symbol: 'AR$', name: 'Argentine Peso',         flag: '🇦🇷', locale: 'es-AR' },
  { code: 'CLP', symbol: 'CLP$',name: 'Chilean Peso',           flag: '🇨🇱', locale: 'es-CL' },
  { code: 'COP', symbol: 'COL$',name: 'Colombian Peso',         flag: '🇨🇴', locale: 'es-CO' },
  { code: 'PEN', symbol: 'S/.', name: 'Peruvian Sol',           flag: '🇵🇪', locale: 'es-PE' },
  { code: 'UYU', symbol: '$U',  name: 'Uruguayan Peso',         flag: '🇺🇾', locale: 'es-UY' },
  { code: 'PYG', symbol: '₲',   name: 'Paraguayan Guarani',     flag: '🇵🇾', locale: 'es-PY' },
  { code: 'DOP', symbol: 'RD$', name: 'Dominican Peso',         flag: '🇩🇴', locale: 'es-DO' },

  // --- Europe ---
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc',            flag: '🇨🇭', locale: 'de-CH' },
  { code: 'SEK', symbol: 'kr',  name: 'Swedish Krona',          flag: '🇸🇪', locale: 'sv-SE' },
  { code: 'NOK', symbol: 'kr',  name: 'Norwegian Krone',        flag: '🇳🇴', locale: 'nb-NO' },
  { code: 'DKK', symbol: 'kr',  name: 'Danish Krone',           flag: '🇩🇰', locale: 'da-DK' },
  { code: 'ISK', symbol: 'kr',  name: 'Icelandic Króna',        flag: '🇮🇸', locale: 'is-IS' },
  { code: 'PLN', symbol: 'zł',  name: 'Polish Zloty',           flag: '🇵🇱', locale: 'pl-PL' },
  { code: 'CZK', symbol: 'Kč',  name: 'Czech Koruna',           flag: '🇨🇿', locale: 'cs-CZ' },
  { code: 'HUF', symbol: 'Ft',  name: 'Hungarian Forint',       flag: '🇭🇺', locale: 'hu-HU' },
  { code: 'RON', symbol: 'lei', name: 'Romanian Leu',           flag: '🇷🇴', locale: 'ro-RO' },
  { code: 'BGN', symbol: 'лв',  name: 'Bulgarian Lev',          flag: '🇧🇬', locale: 'bg-BG' },
  { code: 'HRK', symbol: 'kn',  name: 'Croatian Kuna',          flag: '🇭🇷', locale: 'hr-HR' },
  { code: 'RSD', symbol: 'дин',name: 'Serbian Dinar',          flag: '🇷🇸', locale: 'sr-RS' },
  { code: 'UAH', symbol: '₴',   name: 'Ukrainian Hryvnia',      flag: '🇺🇦', locale: 'uk-UA' },
  { code: 'TRY', symbol: '₺',   name: 'Turkish Lira',           flag: '🇹🇷', locale: 'tr-TR' },
  { code: 'RUB', symbol: '₽',   name: 'Russian Ruble',          flag: '🇷🇺', locale: 'ru-RU' },
  { code: 'GEL', symbol: '₾',   name: 'Georgian Lari',          flag: '🇬🇪', locale: 'ka-GE' },

  // --- APAC ---
  { code: 'AUD', symbol: 'A$',  name: 'Australian Dollar',      flag: '🇦🇺', locale: 'en-AU' },
  { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar',     flag: '🇳🇿', locale: 'en-NZ' },
  { code: 'SGD', symbol: 'S$',  name: 'Singapore Dollar',       flag: '🇸🇬', locale: 'en-SG' },
  { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar',       flag: '🇭🇰', locale: 'en-HK' },
  { code: 'KRW', symbol: '₩',   name: 'Korean Won',             flag: '🇰🇷', locale: 'ko-KR' },
  { code: 'CNY', symbol: '¥',   name: 'Chinese Yuan',           flag: '🇨🇳', locale: 'zh-CN' },
  { code: 'TWD', symbol: 'NT$', name: 'New Taiwan Dollar',      flag: '🇹🇼', locale: 'zh-TW' },
  { code: 'INR', symbol: '₹',   name: 'Indian Rupee',           flag: '🇮🇳', locale: 'en-IN' },
  { code: 'IDR', symbol: 'Rp',  name: 'Indonesian Rupiah',      flag: '🇮🇩', locale: 'id-ID' },
  { code: 'THB', symbol: '฿',   name: 'Thai Baht',              flag: '🇹🇭', locale: 'th-TH' },
  { code: 'PHP', symbol: '₱',   name: 'Philippine Peso',        flag: '🇵🇭', locale: 'en-PH' },
  { code: 'VND', symbol: '₫',   name: 'Vietnamese Dong',        flag: '🇻🇳', locale: 'vi-VN' },
  { code: 'MYR', symbol: 'RM',  name: 'Malaysian Ringgit',      flag: '🇲🇾', locale: 'ms-MY' },
  { code: 'KHR', symbol: '៛',   name: 'Cambodian Riel',         flag: '🇰🇭', locale: 'km-KH' },
  { code: 'BDT', symbol: '৳',   name: 'Bangladeshi Taka',       flag: '🇧🇩', locale: 'bn-BD' },
  { code: 'PKR', symbol: '₨',   name: 'Pakistani Rupee',        flag: '🇵🇰', locale: 'ur-PK' },
  { code: 'LKR', symbol: 'Rs',  name: 'Sri Lankan Rupee',       flag: '🇱🇰', locale: 'si-LK' },
  { code: 'KZT', symbol: '₸',   name: 'Kazakhstani Tenge',      flag: '🇰🇿', locale: 'kk-KZ' },
  { code: 'UZS', symbol: 'soʻm',name: 'Uzbekistani Som',        flag: '🇺🇿', locale: 'uz-UZ' },

  // --- MENA ---
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham',             flag: '🇦🇪', locale: 'ar-AE' },
  { code: 'SAR', symbol: 'ر.س', name: 'Saudi Riyal',            flag: '🇸🇦', locale: 'ar-SA' },
  { code: 'QAR', symbol: 'ر.ق', name: 'Qatari Riyal',           flag: '🇶🇦', locale: 'ar-QA' },
  { code: 'KWD', symbol: 'د.ك', name: 'Kuwaiti Dinar',          flag: '🇰🇼', locale: 'ar-KW' },
  { code: 'BHD', symbol: '.د.ب',name: 'Bahraini Dinar',         flag: '🇧🇭', locale: 'ar-BH' },
  { code: 'OMR', symbol: 'ر.ع',name: 'Omani Rial',             flag: '🇴🇲', locale: 'ar-OM' },
  { code: 'JOD', symbol: 'د.ا', name: 'Jordanian Dinar',        flag: '🇯🇴', locale: 'ar-JO' },
  { code: 'LBP', symbol: 'ل.ل', name: 'Lebanese Pound',         flag: '🇱🇧', locale: 'ar-LB' },
  { code: 'EGP', symbol: 'ج.م',name: 'Egyptian Pound',         flag: '🇪🇬', locale: 'ar-EG' },
  { code: 'ILS', symbol: '₪',   name: 'Israeli New Shekel',     flag: '🇮🇱', locale: 'he-IL' },
  { code: 'MAD', symbol: 'DH',  name: 'Moroccan Dirham',        flag: '🇲🇦', locale: 'ar-MA' },

  // --- Africa ---
  { code: 'ZAR', symbol: 'R',   name: 'South African Rand',     flag: '🇿🇦', locale: 'en-ZA' },
  { code: 'NGN', symbol: '₦',   name: 'Nigerian Naira',         flag: '🇳🇬', locale: 'en-NG' },
  { code: 'KES', symbol: 'KSh', name: 'Kenyan Shilling',        flag: '🇰🇪', locale: 'en-KE' },
  { code: 'GHS', symbol: 'GH₵', name: 'Ghanaian Cedi',          flag: '🇬🇭', locale: 'en-GH' },
  { code: 'TZS', symbol: 'TSh', name: 'Tanzanian Shilling',     flag: '🇹🇿', locale: 'sw-TZ' },
];

export const CURRENCY_BY_CODE: Record<string, CurrencyMeta> = Object.fromEntries(
  CURRENCIES.map((c) => [c.code, c]),
);

/** Best-effort default for a country code. */
export function currencyForCountry(country: string | null | undefined): string {
  if (!country) return 'USD';
  const c = country.toUpperCase();
  const map: Record<string, string> = {
    // Eurozone — explicit, so the picker doesn't surprise users in IT/NL/etc.
    DE: 'EUR', FR: 'EUR', ES: 'EUR', IT: 'EUR', NL: 'EUR', BE: 'EUR',
    AT: 'EUR', IE: 'EUR', PT: 'EUR', FI: 'EUR', GR: 'EUR', LU: 'EUR',
    SK: 'EUR', SI: 'EUR', EE: 'EUR', LV: 'EUR', LT: 'EUR', MT: 'EUR', CY: 'EUR', HR: 'EUR',
    // 1:1 country↔currency
    JP: 'JPY', US: 'USD', GB: 'GBP',
    KR: 'KRW', CN: 'CNY', TW: 'TWD', HK: 'HKD', MO: 'HKD', SG: 'SGD',
    CH: 'CHF', CA: 'CAD', AU: 'AUD', NZ: 'NZD',
    IN: 'INR', ID: 'IDR', TH: 'THB', PH: 'PHP', VN: 'VND', MY: 'MYR', KH: 'KHR',
    BR: 'BRL', MX: 'MXN', AR: 'ARS', CL: 'CLP', CO: 'COP', PE: 'PEN',
    UY: 'UYU', PY: 'PYG', DO: 'DOP',
    AE: 'AED', SA: 'SAR', QA: 'QAR', KW: 'KWD', BH: 'BHD', OM: 'OMR',
    JO: 'JOD', LB: 'LBP', EG: 'EGP', IL: 'ILS', MA: 'MAD',
    ZA: 'ZAR', NG: 'NGN', KE: 'KES', GH: 'GHS', TZ: 'TZS',
    SE: 'SEK', NO: 'NOK', DK: 'DKK', IS: 'ISK',
    PL: 'PLN', CZ: 'CZK', HU: 'HUF', RO: 'RON', BG: 'BGN', RS: 'RSD',
    UA: 'UAH', TR: 'TRY', RU: 'RUB', GE: 'GEL',
    BD: 'BDT', PK: 'PKR', LK: 'LKR', KZ: 'KZT', UZ: 'UZS',
  };
  return map[c] ?? 'USD';
}
